import { Device } from "../../../model/Device";
import { getServerDate } from "../../../utils/storage/date";
import { TMultiCallParam } from "../../../utils/api.interfaces";
import Api from "../../../services/api";
import { IVehicleListItem } from "../../../ui/assets/helper";
import { dateTimeComparator } from "../../../utils/date";
import { runMultiCallSearch } from "./trailersHelper";

const MAX_SEARCH_RESULTS = 20;
const NAME_MATCH_RESULTS = 5;

interface ILastUsedVehicleRecord {
    device: IIdEntity;
    dateTime: string;
}

/**
 * Format a device into a vehicle list item for display
 */
export function formatVehicleForDisplay(device: Device | IDevice, subtitle = ""): IVehicleListItem {
    const deviceObj = device instanceof Device ? device : new Device(device);
    const licensePlate = deviceObj.licensePlate ? ` (${ deviceObj.licensePlate })` : "";

    return {
        name: `${ deviceObj.name }${ licensePlate }`,
        subtitle,
        device: deviceObj
    };
}

/**
 * Search for vehicles by name, VIN, license plate, or serial number.
 *
 * Uses a two-pass multiCall strategy to ensure exact and partial name matches are not crowded out
 * by serial-number or metadata matches when many devices share a common substring:
 *   - Pass 1: broad keyword search across all 9 metadata fields (VIN, serial number, name, etc.)
 *   - Pass 2: name-only search to surface devices whose name matches the term
 *
 * Results are merged (name-search results first), deduplicated by ID, and sorted so that the
 * exact name match appears at the top, followed by the remaining results alphabetically.
 * This mirrors the two-pass approach used by searchTrailers in trailersHelper.ts.
 */
export function searchVehicles(api: Api, searchTerm: string): Promise<IVehicleListItem[]> {
    const fromDate = getServerDate().toISOString();

    // Pass 1: broad keyword search across all metadata fields
    const calls: TMultiCallParam[] = [
        ["Get", {
            typeName: "Device",
            resultsLimit: MAX_SEARCH_RESULTS,
            search: {
                keywords: [`%${ searchTerm }%`],
                fromDate,
                groups: [{ id: "GroupVehicleId" }]
            }
        }]
    ];

    // Pass 2: name-only search to elevate name matches above metadata-only matches
    if (searchTerm) {
        calls.push(["Get", {
            typeName: "Device",
            resultsLimit: NAME_MATCH_RESULTS,
            search: {
                name: searchTerm,
                fromDate,
                groups: [{ id: "GroupVehicleId" }]
            }
        }]);
    }

    return runMultiCallSearch<IDevice>(api, calls, searchTerm).then((devices: IDevice[]) => {
        return devices.map(device => formatVehicleForDisplay(device));
    });
}

/**
 * Filter out archived vehicles (inactive or with placeholder serial number)
 */
export function filterArchivedVehicles(vehicles: IVehicleListItem[]): IVehicleListItem[] {
    const now = getServerDate();
    return vehicles.filter((vehicle) => {
        // Filter out archived vehicles (inactive or with placeholder serial number)
        const activeTo = vehicle.device.activeTo;
        const isActive = !activeTo || activeTo > now;
        const hasValidSerial = vehicle.device.serialNumber?.localeCompare("000-000-0000") !== 0;
        return isActive && hasValidSerial;
    });
}

/**
 * Format nearby vehicles with distance information
 * @param assets - Array of device objects
 * @param distances - Map of device ID to distance in km
 * @param isMetric - Whether to use metric units
 * @param proximityString - Translation string like "{0} away"
 * @param excludeIds - Optional set of device IDs to exclude from results
 */
export function formatNearbyVehicles(
    assets: IDevice[],
    distances: IIdDictionary<number>,
    isMetric: boolean,
    proximityString: string,
    excludeIds?: Set<string>
): IVehicleListItem[] {
    let nearestVehicles: IVehicleListItem[] = [];

    assets.forEach((asset: IDevice) => {
        const device = new Device(asset);

        let distance = distances && distances[asset.id] ? distances[asset.id] : undefined;
        if (distance !== undefined && !isMetric) {
            distance = distance * 0.621371; // Convert to miles
        }

        const distanceLabel = distance !== undefined
            ? ` ~ ${ proximityString.replace("{0}", `${ distance.toFixed(1) } ${ isMetric ? "km" : "mi" }`) }`
            : "";

        nearestVehicles.push({
            name: `${ asset.name }${ device.licensePlate ? ` (${ device.licensePlate })` : "" }`,
            subtitle: distanceLabel,
            device,
            distance
        });
    });

    // Sort by distance, then by name
    nearestVehicles.sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined && a.distance !== b.distance) {
            return a.distance > b.distance ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
    });

    // Filter out excluded vehicles
    if (excludeIds && excludeIds.size > 0) {
        nearestVehicles = nearestVehicles.filter(vehicle =>
            vehicle.device && !excludeIds.has(vehicle.device.id));
    }

    return nearestVehicles;
}

/**
 * Find the last used vehicle by checking recent trips and HOS logs
 * Returns the most recent record with device info
 */
export function findLastUsedVehicleRecord(
    api: Api,
    userId: string
): Promise<ILastUsedVehicleRecord | null> {
    const nowDateString = getServerDate().toISOString();

    return api.multiCallPromise([
        ["Get", {
            typeName: "Trip",
            search: {
                userSearch: { id: userId },
                fromDate: nowDateString,
                includeOverlappedTrips: true
            },
            resultsLimit: 1,
            propertySelector: {
                fields: ["device", "stop"],
                isIncluded: true
            }
        }],
        ["Get", {
            typeName: "DutyStatusLog",
            search: {
                userSearch: { id: userId },
                fromDate: nowDateString,
                includeBoundaryLogs: true,
                includeLocations: false
            },
            resultsLimit: 1,
            propertySelector: {
                fields: ["device", "dateTime"],
                isIncluded: true
            }
        }]
    ]).then(results => {
        const records: ILastUsedVehicleRecord[] = [];

        const trip = results[0].length > 0 ? results[0][0] : null;
        if (trip) {
            records.push({
                device: trip.device,
                dateTime: trip.stop
            });
        }

        const hosLog = results[1].length > 0 ? results[1][0] : null;
        if (hosLog) {
            records.push({
                device: hosLog.device,
                dateTime: hosLog.dateTime
            });
        }

        if (records.length === 0) {
            return null;
        }

        // Sort by dateTime descending (most recent first)
        records.sort(dateTimeComparator(false));
        return records[0];
    }).catch(() => null);
}

// Re-export the interface for convenience
export type { IVehicleListItem } from "../../../ui/assets/helper";

// Export the internal interface for consumers
export type { ILastUsedVehicleRecord };

const MAX_NEARBY_VEHICLES = 20;

/**
 * Options for loading suggested vehicles
 */
export interface ILoadSuggestedVehiclesOptions {
    api: Api;
    deviceCache: {
        getOrFetch: (api: Api, devices: IIdEntity[], userId: string) => Promise<IDevice[][] | null>;
    };
    vehicleProximity?: {
        getAssignmentInfo: (limit: number, includeDistance: boolean, includeDriver: boolean) => Promise<{
            statuses: { device: IIdEntity; distance: number }[];
        } | null>;
    };
    userId: string;
    isMetric: boolean;
    currentVehicleId?: string;
    lastUsedText: string;
    distanceAwayText: string;
}

/**
 * Load suggested vehicles (last used and nearby)
 * @param options - Configuration for loading vehicles
 * @param abortSignal - Optional abort function that returns true if operation should abort
 * @returns Array of suggested vehicle list items
 */
export async function loadSuggestedVehicles(
    options: ILoadSuggestedVehiclesOptions,
    abortSignal?: () => boolean
): Promise<IVehicleListItem[]> {
    const {
        api,
        deviceCache,
        vehicleProximity,
        userId,
        isMetric,
        currentVehicleId,
        lastUsedText,
        distanceAwayText
    } = options;

    const allVehicles: IVehicleListItem[] = [];
    const excludeIds = new Set<string>();

    // Add current vehicle ID to exclude list
    if (currentVehicleId) {
        excludeIds.add(currentVehicleId);
    }

    // 1. Try to find last used vehicle
    const lastUsedRecord = await findLastUsedVehicleRecord(api, userId);
    if (abortSignal?.()) {
        return [];
    }

    if (lastUsedRecord?.device) {
        const deviceResults = await deviceCache.getOrFetch(api, [lastUsedRecord.device], userId);
        if (abortSignal?.()) {
            return [];
        }

        if (deviceResults?.[0]?.[0]) {
            const device = new Device(deviceResults[0][0]);
            const lastUsedVehicle = formatVehicleForDisplay(device, lastUsedText);
            allVehicles.push(lastUsedVehicle);
            excludeIds.add(device.id);
        }
    }

    // 2. Find nearby vehicles using vehicle proximity service
    if (vehicleProximity && !abortSignal?.()) {
        try {
            const proximityData = await vehicleProximity.getAssignmentInfo(MAX_NEARBY_VEHICLES, true, false);
            if (abortSignal?.()) {
                return [];
            }

            if (proximityData?.statuses && proximityData.statuses.length > 0) {
                const deviceDistances: IIdDictionary<number> = {};
                const nearbyDeviceIds: IIdEntity[] = [];

                proximityData.statuses.forEach(status => {
                    if (status.distance <= 1) { // Within 1km
                        deviceDistances[status.device.id] = status.distance;
                        nearbyDeviceIds.push(status.device);
                    }
                });

                if (nearbyDeviceIds.length > 0) {
                    const batchCalls: TMultiCallParam[] = nearbyDeviceIds.map(device => ["Get", {
                        typeName: "Device",
                        search: { id: device.id },
                        propertySelector: { fields: ["id", "name", "licensePlate", "activeTo", "serialNumber"], isIncluded: true }
                    }]);
                    const deviceResults = await api.multiCallPromise(batchCalls);
                    const devices: IDevice[] = ([] as IDevice[]).concat(...(deviceResults || [])).filter(Boolean);

                    const nearbyVehicles = formatNearbyVehicles(
                        devices,
                        deviceDistances,
                        isMetric,
                        distanceAwayText,
                        excludeIds
                    );

                    allVehicles.push(...nearbyVehicles);
                }
            }
        } catch {
            // Proximity service may not be available, continue without nearby vehicles
        }
    }

    // Filter out archived vehicles
    if (abortSignal?.()) {
        return [];
    }

    return filterArchivedVehicles(allVehicles);
}
